import { useCallback } from "react";

import { IHttpRequestConfig } from "@/browser";
import { useCancelTokenSource } from "@/hooks/http";

import { ITypeaheadProps } from "../components";

export interface ITypeaheadSearchParams {
  query: string;
}

type TTypeaheadFetchFn<Option, SearchParams> = (
  query: SearchParams & ITypeaheadSearchParams,
  options?: IHttpRequestConfig,
) => Promise<Option[]>;

type TTypeaheadLoadOptionsFn<Option> = ITypeaheadProps<Option>["loadOptions"];

export interface IUseLoadOptionsParams<Option, SearchParams> {
  fetchData: TTypeaheadFetchFn<Option, SearchParams>;
  filterAfterFetch?: (options: Option[]) => Option[];
  searchParams?: SearchParams;
}

export function useLoadOptions<Option, SearchParams = void>(params: IUseLoadOptionsParams<Option, SearchParams>) {
  const { fetchData, filterAfterFetch, searchParams } = params;
  const { refreshToken, source } = useCancelTokenSource();

  const loadOptions: TTypeaheadLoadOptionsFn<Option> = useCallback(
    (input, callback) => {
      refreshToken();

      const query = { query: input, ...searchParams } as SearchParams & ITypeaheadSearchParams;

      return fetchData(query, { cancelToken: source.current.token }).then(payload => {
        const options = filterAfterFetch ? filterAfterFetch(payload) : payload;
        callback(options);
      });
    },
    [fetchData, filterAfterFetch, searchParams],
  );

  return { loadOptions };
}
